import React, { FC } from 'react';
import { MUIFieldProps } from '../../../../../lib/types/types';
import { get } from 'lodash';
import {
	Box,
	Button,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import FormFields from '../FormFields';
import { set } from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { enumT, isTrue, T } from '../../../../../utils/helper';
import { DisplayDate, DisplayEuro } from '../../../../../utils/formats';
import { mapTablePath } from '../../../../../utils/formStruc';

const EditableTable: FC<MUIFieldProps> = (props) => {
	const { fieldValue, updateFieldValue, isDisabled } = props;

	const columns = get(props, 'schema.attributes');
	const isFieldDisabled = (columnKey: string) => {
		if (isDisabled) return true;
		if (get(props, 'disabledColumns', []).includes(columnKey)) {
			return true;
		} else return false;
	};
	const tablePath = get(props, 'path', '');

	const handleAddRow = () => {
		let newValue = Object.keys(columns).map((key) => {
			return { [key]: null };
		});
		set(fieldValue, `${fieldValue.length}`, ...newValue);

		let e = { target: { value: fieldValue } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};
	const handleChange = (path: any, value: any, rowIndex: number) => {
		set(fieldValue, `${rowIndex}.${path}`, value);

		let e = { target: { value: fieldValue } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const handleDelete = (rowIndex: number) => {
		const newRow = fieldValue.filter((_: any, index: number) => index !== rowIndex);

		let e = { target: { value: newRow } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const visibleColumns = Object.keys(columns).filter((key: string) => !get(props, 'hideColumns', []).includes(key));

	return (
		<>
			<Box sx={{ mt: '1rem' }}>
				{!get(props, 'hideAddButton') && (
					<Stack justifyContent={'flex-end'} sx={{ mb: '0.5rem' }}>
						<Button
							sx={{ marginLeft: 'auto' }}
							endIcon={<AddIcon />}
							onClick={handleAddRow}
							disabled={isDisabled || fieldValue.length >= get(props, 'maxLevel', 999)}
						>
							{' '}
							Hinzufügen
						</Button>
					</Stack>
				)}
				<TableContainer component={'div'}>
					<Table sx={{ tableLayout: 'fixed', width: '100%' }} className='editable-table'>
						<TableHead>
							<TableRow>
								{visibleColumns.map((key) => {
									const colDef = columns[key];
									return (
										<TableCell
											key={key}
											align={isDisabled && ['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
										>
											{getColumnLabel(get(colDef, 'path'), key, get(props, 'fieldProps'))}
										</TableCell>
									);
								})}
								{!get(props, 'hideDeleteButton') && (
									<TableCell sx={{ width: '80px', borderLeft: '2px solid #EAF0F1' }}>
										{' '}
										Aktion
									</TableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{(fieldValue || []).map((eachRow: any, rowIndex: number) => {
								return (
									<TableRow key={rowIndex}>
										{visibleColumns.map((columnKey) => {
											const colDef = columns[columnKey];
											const attributePathWithIndex = mapTablePath(colDef.path, rowIndex, tablePath);
											return (
												<TableCell
													key={columnKey}
													sx={{
														padding: '0 1rem',
														backgroundColor: `${isDisabled || get(props, 'disabledColumns', []).includes(columnKey) ? 'background.paper' : 'unset'}`,
													}}
													align={['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
												>
													{isFieldDisabled(columnKey) ? (
														<Typography>
															{getDisplayText(
																colDef.type,
																eachRow[columnKey],
																get(props, `fieldProps.${columnKey}.format`),
															)}
														</Typography>
													) : (
														<FormFields
															label=""
															type={colDef.type}
															formValues={eachRow}
															format={get(props, `fieldProps.${columnKey}.format`)}
															path={columnKey}
															onChange={(path, value) => handleChange(path, value, rowIndex)}
															disabled={isDisabled || get(props, 'disabledColumns', []).includes(columnKey)}
															editableTablePathWIthIndex={attributePathWithIndex}
														/>
													)}
												</TableCell>
											);
										})}

										{!get(props, 'hideDeleteButton') && (
											<TableCell sx={{ width: '80px', borderLeft: '2px solid #EAF0F1' }}>
												<IconButton onClick={() => handleDelete(rowIndex)} disabled={isDisabled}>
													<DeleteIcon />
												</IconButton>
											</TableCell>
										)}
									</TableRow>
								);
							})}
							{get(props, 'sumRow', []).map((lastRow: any, index: number) => {
								return (
									<TableRow key={index} sx={{ backgroundColor: 'background.paper' }}>
										{visibleColumns.map((columnKey) => {
											const colDef = columns[columnKey];
											return (
												<TableCell
													key={columnKey}
													sx={{ padding: '1rem' }}
													align={['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
												>
													<Typography>
														{getDisplayText(
															colDef.type,
															lastRow[columnKey],
															get(props, `fieldProps.${columnKey}.format`),
														)}
													</Typography>
												</TableCell>
											);
										})}
										<TableCell></TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</>
	);
};
export default EditableTable;

function getColumnLabel(path: string, key: string, fieldProps: any): string {
	if (fieldProps && fieldProps[key] && fieldProps[key].label) {
		return fieldProps[key].label;
	}

	const parts: string[] = path.split('.');
	const lastPart: string = parts.pop()!;

	if (lastPart.startsWith('Zelle')) {
		// Extract the number from the last part
		const zelleNumber: string = lastPart.match(/\d+/)![0];
		// Modify the second to last part, which contains Block_X
		let blockPart: string = parts.pop()!;
		blockPart = blockPart.includes('Block_') ? blockPart : blockPart + '_Block_' + zelleNumber;

		const modifiedBlockPart: string = blockPart.replace(/Block_\d+/, `Spalte_${zelleNumber}`);
		return T(modifiedBlockPart);
	} else {
		return T(lastPart);
	}
}

function getDisplayText(type: string, value: any, format?: string) {
	switch (type) {
		case 'decimal':
			if (format === 'currency') {
				return DisplayEuro(value);
			}
			return value;
		case 'date':
			return DisplayDate(value);
		case 'enumeration':
			return enumT(value);
		case 'boolean':
			return isTrue(value) ? 'Ja' : 'Nein';
		default:
			return value;
	}
}
